import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg, fmtDate, fmtDateTime, pretty, tzs, todayISO, PAY_CHANNELS } from "@/lib/api";
import InvoiceForm from "@/components/billing/InvoiceForm";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const Lines = ({ inv }) => (
  <div className="overflow-x-auto rounded-md border">
    <table className="w-full text-sm" data-testid="invoice-lines-table">
      <thead className="bg-muted text-left text-xs uppercase text-muted-foreground"><tr><th className="p-2">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Discount</th><th className="p-2">Tax</th><th className="p-2 text-right">Total</th></tr></thead>
      <tbody>{inv.lines.map((l, i) => (
        <tr key={i} className="border-t"><td className="p-2">{l.description}</td><td className="p-2 text-right">{l.quantity}</td><td className="p-2 text-right">{tzs(l.unit_price)}</td><td className="p-2 text-right">{tzs(l.discount)}</td><td className="p-2 text-xs">{l.tax_name}{l.tax_rate ? ` ${l.tax_rate}%` : ""}</td><td className="p-2 text-right font-medium">{tzs(l.total)}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

const Totals = ({ inv }) => (
  <div className="ml-auto w-full max-w-xs space-y-1 text-sm" data-testid="invoice-totals">
    {[["Subtotal", inv.subtotal], ["Discounts", -inv.discount_total], ["Tax", inv.tax_total]].map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{tzs(v)}</span></div>)}
    <div className="flex justify-between border-t pt-1 font-bold"><span>Total</span><span data-testid="invoice-total">{tzs(inv.total)}</span></div>
    <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{tzs(inv.amount_paid)}</span></div>
    <div className="flex justify-between font-bold text-red-700"><span>Balance</span><span data-testid="invoice-balance">{tzs(inv.balance)}</span></div>
  </div>
);

function IssueBox({ inv, done }) {
  const [ok, setOk] = useState(false);
  const issue = () => api.post(`/billing/invoices/${inv.id}/issue`, { tax_confirmed: ok }).then((r) => { toast.success(`Issued ${r.data.number}`); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="invoice-issue-box">
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="invoice-tax-confirm-checkbox" />
        <span>I confirm the tax treatment on every line is correct for this client ({tzs(inv.tax_total)} tax). Issuing assigns a permanent invoice number and notifies the client owner.</span></label>
      <Button size="sm" disabled={!ok} onClick={issue} data-testid="invoice-issue-btn">Issue invoice</Button>
    </div>
  );
}

function PaymentBox({ inv, done }) {
  const [f, setF] = useState({ amount: inv.balance, paid_on: todayISO(), channel: "bank_transfer", reference: "", internal_note: "" });
  const save = () => api.post(`/billing/invoices/${inv.id}/payments`, { ...f, amount: Number(f.amount) })
    .then((r) => { toast.success(`Recorded · ${r.data.payment.receipt_number}`); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="payment-form">
      <p className="text-sm font-semibold">Record manual (offline) payment</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Amount (TZS)"><Input type="number" min="0" step="any" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
        <Field label="Date received"><Input type="date" max={todayISO()} value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} data-testid="payment-date-input" /></Field>
        <Field label="Channel"><Select value={f.channel} onValueChange={(v) => setF({ ...f, channel: v })}><SelectTrigger data-testid="payment-channel-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PAY_CHANNELS.map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Reference"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="Bank / M-Pesa ref" data-testid="payment-reference-input" /></Field>
      </div>
      <Field label="Internal note (V.A only)"><Textarea value={f.internal_note} onChange={(e) => setF({ ...f, internal_note: e.target.value })} data-testid="payment-note-input" /></Field>
      <Button size="sm" onClick={save} disabled={!f.reference.trim() || Number(f.amount) <= 0} data-testid="payment-save-btn">Record payment &amp; issue receipt</Button>
    </div>
  );
}

function ReasonAction({ label, testId, onSubmit, destructive = true }) {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState("");
  if (!open) return <Button size="sm" variant={destructive ? "destructive" : "outline"} onClick={() => setOpen(true)} data-testid={testId}>{label}</Button>;
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row">
      <Input value={r} onChange={(e) => setR(e.target.value)} placeholder="Reason (required)" data-testid={`${testId}-reason`} />
      <Button size="sm" variant="destructive" disabled={r.trim().length < 5} onClick={() => onSubmit(r)} data-testid={`${testId}-confirm`}>Confirm</Button>
    </div>
  );
}

function Payments({ pays, admin, done }) {
  if (!pays.length) return null;
  const reverse = (p, reason) => api.post(`/billing/payments/${p.id}/reverse`, { reason }).then(() => { toast.success("Payment reversed"); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-2" data-testid="invoice-payments">
      <p className="text-sm font-semibold">Payments</p>
      {pays.map((p) => (
        <div key={p.id} className="space-y-2 rounded-md border p-3 text-sm" data-testid={`payment-row-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><span className="font-mono font-semibold">{p.receipt_number}</span> · {tzs(p.amount)} · {pretty(p.channel)} · ref {p.reference} · {fmtDate(p.paid_on)}</div>
            <div className="flex items-center gap-2"><StatusBadge kind="billing" status={p.status} />
              <a href={`/billing/receipt/${p.id}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid={`receipt-link-${p.id}`}>Receipt</a></div>
          </div>
          <div className="text-xs text-muted-foreground">Manually recorded by {p.recorded_by} on {fmtDateTime(p.recorded_at)}{p.internal_note ? ` · Note: ${p.internal_note}` : ""}{p.reversal_reason ? ` · Reversed: ${p.reversal_reason}` : ""}</div>
          {admin && p.status === "recorded" && <ReasonAction label="Reverse" testId={`payment-reverse-${p.id}`} onSubmit={(r) => reverse(p, r)} />}
        </div>
      ))}
    </div>
  );
}

export default function InvoiceDetail({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const c = (k) => can(user, k);
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => api.get(`/billing/invoices/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); }), [id, onClose]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged?.(); };
  if (!d) return null;
  const inv = d.invoice;
  const act = (p, msg) => p.then(() => { toast.success(msg); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="invoice-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2"><span data-testid="invoice-number">{inv.number || "Draft invoice"}</span><StatusBadge kind="billing" status={inv.display_status} testId="invoice-status" /></DialogTitle>
          <DialogDescription>{inv.company_name} · {inv.issue_date ? `Issued ${fmtDate(inv.issue_date)} · ` : ""}Due {fmtDate(inv.due_date)}</DialogDescription>
        </DialogHeader>
        <Lines inv={inv} />
        <Totals inv={inv} />
        {inv.notes && <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">{inv.notes}</p>}
        {inv.internal_notes && <p className="whitespace-pre-wrap rounded-md border border-dashed p-3 text-xs text-muted-foreground">Internal: {inv.internal_notes}</p>}
        {inv.void_reason && <p className="text-sm text-red-700">Voided: {inv.void_reason}</p>}
        <div className="flex flex-wrap gap-2">
          {["issued", "partially_paid"].includes(inv.status) && inv.balance > 0 && <WhatsAppButton kind="invoice" id={inv.id} label="Payment reminder (WhatsApp)" />}
          {inv.status !== "draft" && <Button size="sm" variant="outline" onClick={() => window.open(`/billing/invoice/${inv.id}`, "_blank")} data-testid="invoice-print-btn"><Printer className="mr-1 h-4 w-4" /> Print / save PDF</Button>}
          {c("invoices_create") && inv.status === "draft" && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="invoice-edit-btn">Edit draft</Button>}
          {c("invoices_create") && inv.status === "draft" && <Button size="sm" variant="ghost" onClick={() => act(api.delete(`/billing/invoices/${inv.id}`), "Draft discarded").then(onClose)} data-testid="invoice-discard-btn">Discard draft</Button>}
          {c("invoices_void") && ["issued", "partially_paid", "paid"].includes(inv.status) && <ReasonAction label="Void invoice" testId="invoice-void-btn" onSubmit={(reason) => act(api.post(`/billing/invoices/${inv.id}/void`, { reason }), "Invoice voided")} />}
        </div>
        {c("invoices_issue") && inv.status === "draft" && <IssueBox inv={inv} done={done} />}
        {c("payments_record") && ["issued", "partially_paid"].includes(inv.status) && <PaymentBox key={inv.balance} inv={inv} done={done} />}
        <Payments pays={d.payments} admin={c("payments_reverse")} done={done} />
        <details className="text-xs text-muted-foreground" data-testid="invoice-history"><summary className="cursor-pointer">History</summary>
          {inv.history.map((h, i) => <div key={i} className="mt-1">{fmtDateTime(h.at)} · {pretty(h.event)} · {h.by}{h.note ? ` — ${h.note}` : ""}</div>)}</details>
        {editing && <InvoiceForm invoice={inv} onClose={() => setEditing(false)} onSaved={done} />}
      </DialogContent>
    </Dialog>
  );
}
